/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import * as stagesCase from "../../../Stages/StagesCase";
import { STAGE_ACTION_REQUIRED } from "../../../Stages/StagesCase";
import personFields from "../modules/personFields";
import { dateFieldDefault, ibanFieldDefault } from "../../../validationRules";
import { trueFalseDefaultOptions } from "../formDefinitionFunctions";
import addressFields from "../modules/addressFields";
import InputAdornment from "@mui/material/InputAdornment";
import React from "react";
import { getProductSpecificDefinition, hasActiveCourtAppointment } from "../../ProductService/productService";
import _ from "lodash";
import { arrayToSelectOptions } from "../../../formServiceFunctions";
import { IbanInput } from "../../../../components/MaskedInputs/IbanInput";
import { allDataPageCourtFields } from "../allDataPageCourtFields";
import { objectivesLawFirm } from "../modules/objectiveLawfirmValues";
import { BackofficeUser } from "../../../../types/BackofficeUser";
import {
  addProcessParticipant,
  caseInsurance,
  extraJudicialProcedure,
  getAdditionalClientsForm,
  getNextInstanceSection,
  getUnderageClientForm,
  judicialProcedure,
  opponentLawyer,
  opposingLiabilityInsurance,
  opposingParty,
  processParticipantsAuthority,
  processParticipantsOrganizations,
  processParticipantsPeople,
  prosecution,
} from "../abstractLitigationCase/fullFormDefinition";
import { FormElementPropsWithoutProduct } from "../../../../components/Case/CaseForm/FormElement";
import { ContractUnion } from "../../../../types/ContractUnion";
import { getCourtAppointmentFields } from "../modules/courtAppointmentFields";
import { debtCollectionAgencyFields } from "../modules/debtCollectionFields";

export const fullFormDefinition = (product: ContractUnion, currentUser: BackofficeUser) => {
  const contractBaseCreated = {
    isHidden: ({ product }: { product: ContractUnion }) => product.stage! > stagesCase.STAGE_CREATED,
    label: "Allgemeines zum Vertrag",
    elements: [...personFields({ path: `${product.productClassName}.client`, showDiverseGenderOption: true })],
  };

  const contractBase = {
    isHidden: ({ product }: { product: ContractUnion }) => product.stage! < stagesCase.STAGE_DATA_COMPLETED,
    label: "Allgemeines zum Vertrag",
    elements: [
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.caseCategory`,
        isDisabled: ({ product }: { product: ContractUnion }) =>
          product.stage! > STAGE_ACTION_REQUIRED || product.caseCategory === "advisory",
        options: ({ product }: { product: ContractUnion }) => {
          return product.caseCategory === "advisory"
            ? [{ value: "enforceClaim" }, { value: "defendClaim" }, { value: "advisory" }]
            : [{ value: "enforceClaim" }, { value: "defendClaim" }];
        },
        alert: {
          onChange:
            "Das Ändern der Position der Mandantschaft (Klägerin/Beklagte) führt dazu, dass sich viele " +
            "Felder ändern und auch Felder verschwinden. Die neuen Felder müssen dann von Ihnen neu " +
            "befüllt werden, damit der Fall weiter bearbeitet werden kann. Möchten Sie die Position " +
            "der Mandantschaft im Verfahren tatsächlich ändern?",
        },
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.affectedPartyType`,
        isDisabled: () => true,
        options: () => [{ value: "customer" }, { value: "coinsured" }, { value: "unknown" }],
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.contractDate`,
        validators: dateFieldDefault,
        isDisabled: ({ product }: { product: ContractUnion }) => product.stage! >= stagesCase.STAGE_LAWSUIT,
        isHidden: ({ product }: { product: ContractUnion }) => product.advisoryHandling === true,
      },
      ...personFields({ path: `${product.productClassName}.client`, showDiverseGenderOption: true }),
      ...addressFields({
        path: `${product.productClassName}.client.residenceAddress`,
        isHidden: ({ product }: { product: ContractUnion }) => product.advisoryHandling === true,
      }),
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.client.birthDate`,
        validators: dateFieldDefault,
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.objectiveClient`,
        isDisabled: () => true,
        isHidden: ({ product }: { product: ContractUnion }) => product.caseCategory !== "enforceClaim",
        options: () => [
          { value: "compensation" },
          { value: "reduction" },
          { value: "replacement" },
          { value: "repair" },
          { value: "withdrawal" },
          { value: "fulfillment" },
          { value: "other" },
        ],
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.subjectOfDispute`,
        translationPath: `${product.productClassName}.subjectOfDispute.${product.caseCategory}`,
        isDisabled: ({ product }: { product: ContractUnion }) => product.stage! >= stagesCase.STAGE_CHECKED,
        isHidden: ({ product }: { product: ContractUnion }) => product.advisoryHandling === true,
        options: () => [{ value: "money" }, { value: "goods" }, { value: "other" }],
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.clientHasAlreadyClaimed`,
        isDisabled: ({ product }: { product: ContractUnion }) => product.stage! >= stagesCase.STAGE_CHECKED,
        isHidden: ({ product }: { product: ContractUnion }) => product.caseCategory !== "enforceClaim",
        options: () => trueFalseDefaultOptions(`${product.productClassName}.clientHasAlreadyClaimed`),
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.claimedDateClient`,
        validators: dateFieldDefault,
        isHidden: ({ product }: { product: ContractUnion }) => product.caseCategory !== "enforceClaim",
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.claimedDeadlineClient`,
        validators: dateFieldDefault,
        isHidden: ({ product }: { product: ContractUnion }) =>
          product.caseCategory !== "enforceClaim" || !product.clientHasAlreadyClaimed,
      },
      {
        type: "ValidatorNumberField",
        path: `${product.productClassName}.claimedAmountClient`,
        isHidden: ({ product }: { product: ContractUnion }) => product.subjectOfDispute !== "money",
        numberType: "float",
        fieldType: "money",
      },
      {
        type: "ValidatorNumberField",
        path: `${product.productClassName}.overdueFees`,
        isHidden: ({ product }: { product: ContractUnion }) =>
          product.subjectOfDispute !== "money" ||
          (product.caseCategory !== "defendClaim" && !product.clientHasAlreadyClaimed),
        fieldType: "money",
        numberType: "float",
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.client.bankData.iban`,
        validators: ibanFieldDefault,
        additionalProps: {
          InputProps: {
            inputComponent: IbanInput,
            startAdornment: <InputAdornment position="start">DE</InputAdornment>,
          },
        },
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.defenseProcessStatus`,
        isDisabled: ({ product }: { product: ContractUnion }) =>
          product.stage! >= stagesCase.STAGE_DATA_COMPLETED_LAWFIRM,
        isHidden: ({ product }: { product: ContractUnion }) => product.caseCategory !== "defendClaim",
        options: () => [
          { value: "reminder" },
          { value: "debtCollection" },
          { value: "paymentOrder" },
          { value: "enforcementOrder" },
          { value: "lawsuit" },
          { value: "none" },
        ],
      },
    ],
  };

  const contractDetails = {
    isHidden: ({ product }: { product: ContractUnion }) =>
      product.stage! < stagesCase.STAGE_DATA_COMPLETED || product.caseCategory === "advisory",
    label: "Details zum Vertrag",
    elements: getProductSpecificDefinition(product),
  };

  const contractDescription = {
    isHidden: ({ product }: { product: ContractUnion }) => product.stage! < stagesCase.STAGE_DATA_COMPLETED,
    label: "Beschreibung der Situation",
    elements: [
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.personalTextMessage`,
        isDisabled: () => true,
        additionalProps: {
          multiline: true,
          fullWidth: true,
        },
      },
    ],
  };

  const contractLawSpecificFields: FormElementPropsWithoutProduct<ContractUnion>[] = [
    {
      type: "ValidatorTextField",
      path: `${product.productClassName}.additionalIssueDescriptionText`,
      isDisabled: ({ product }: { product: ContractUnion }) => product.stage >= stagesCase.STAGE_CHECKED,
      isHidden: ({ product }: { product: ContractUnion }) =>
        product.advisoryHandling === true ||
        product.isLawsuitHandling ||
        (product.stage >= stagesCase.STAGE_CHECKED && !product.additionalIssueDescriptionText),
      additionalProps: {
        multiline: true,
        fullWidth: true,
      },
    },
    {
      type: "ValidatorTextField",
      path: `${product.productClassName}.additionalClaimText`,
      isDisabled: ({ product }: { product: ContractUnion }) => product.stage >= stagesCase.STAGE_CHECKED,
      isHidden: ({ product }: { product: ContractUnion }) =>
        product.advisoryHandling === true ||
        product.isLawsuitHandling ||
        (product.stage >= stagesCase.STAGE_CHECKED && !product.additionalClaimText),
      additionalProps: {
        multiline: true,
        fullWidth: true,
      },
    },
    // the defectsDescriptionLawFirm field only exist in those Contracts
    // serviceContract
    // purchaseContract
    // vehicleContract
    {
      type: "ValidatorTextField",
      path: `${product.productClassName}.defectsDescriptionLawFirm`,
      isDisabled: ({ product }: { product: ContractUnion }) => product.stage >= stagesCase.STAGE_CHECKED,
      isHidden: ({ product }: { product: ContractUnion }) =>
        !_.has(product, "defectsDescriptionLawFirm") || product.caseCategory !== "enforceClaim",
      additionalProps: {
        multiline: true,
        fullWidth: true,
      },
    },
  ];

  return {
    product: {
      sections: [
        contractBaseCreated,
        contractBase,
        contractDetails,
        contractDescription,
        getAdditionalClientsForm(product),
        getUnderageClientForm(product, "Minderjähriger Vertragspartner"),
      ],
    },
    additional: {
      sections: [
        {
          label: "Allgemeines",
          elements: [
            {
              type: "ValidatorTextField",
              path: `${product.productClassName}.reference`,
              isDisabled: () => true,
            },
            {
              type: "ValidatorTextField",
              path: `${product.productClassName}.powerOfAttorney`,
              isHidden: ({ product }: { product: ContractUnion }) =>
                product.stage! < stagesCase.STAGE_DATA_COMPLETED || product.caseCategory === "advisory",
              isDisabled: () => true,
            },
            {
              type: "ValidatorSelect",
              path: `${product.productClassName}.objectiveLawFirm`,
              isDisabled: ({ product }: { product: ContractUnion }) => product.stage! >= stagesCase.STAGE_CHECKED,
              isHidden: ({ product }: { product: ContractUnion }) => product.advisoryHandling === true,
              options: ({ product }: { product: ContractUnion }) => {
                if (_.isEmpty(product.possibleObjectives)) {
                  return objectivesLawFirm;
                } else {
                  return arrayToSelectOptions(product.possibleObjectives);
                }
              },
            },
            {
              type: "ValidatorSelect",
              path: `${product.productClassName}.objectiveLawFirmSecondary`,
              isDisabled: ({ product }: { product: ContractUnion }) => product.stage! >= stagesCase.STAGE_CHECKED,
              isHidden: ({ product }: { product: ContractUnion }) => product.advisoryHandling === true,
              options: ({ product }: { product: ContractUnion }) => {
                if (_.isEmpty(product.possibleObjectives)) {
                  return objectivesLawFirm;
                } else {
                  return arrayToSelectOptions(product.possibleObjectives);
                }
              },
            },
            {
              type: "ValidatorSelect",
              path: `${product.productClassName}.objectiveLawFirmTertiary`,
              isDisabled: ({ product }: { product: ContractUnion }) => product.stage! >= stagesCase.STAGE_CHECKED,
              isHidden: ({ product }: { product: ContractUnion }) => product.advisoryHandling === true,
              options: ({ product }: { product: ContractUnion }) => {
                if (_.isEmpty(product.possibleObjectives)) {
                  return objectivesLawFirm;
                } else {
                  return arrayToSelectOptions(product.possibleObjectives);
                }
              },
            },
            {
              type: "ValidatorDateField",
              path: `${product.productClassName}.timeBarredDate`,
              validators: dateFieldDefault,
              isHidden: ({ product }: { product: ContractUnion }) =>
                product.stage! < stagesCase.STAGE_DATA_COMPLETED || product.advisoryHandling === true,
              isDisabled: ({ product }: { product: ContractUnion }) => product.stage! >= stagesCase.STAGE_CHECKED,
            },
            {
              type: "ValidatorSelect",
              path: `${product.productClassName}.caseCategoryAssessment`,
              isHidden: ({ product }: { product: ContractUnion }) =>
                product.stage! < stagesCase.STAGE_DATA_COMPLETED || product.caseCategory === "advisory",
              isDisabled: ({ product }: { product: ContractUnion }) =>
                product.stage! > stagesCase.STAGE_WAITING_FOR_FEEDBACK,
              options: () => [{ value: "confirm" }, { value: "reject" }],
            },
          ],
        },
        extraJudicialProcedure(product, currentUser, contractLawSpecificFields),
        judicialProcedure(product),
        {
          label: product.courtAppointments.length === 1 ? "Gerichtstermin" : "Gerichtstermine",
          isHidden: ({ product }: { product: ContractUnion }) => product.courtAppointments.length === 0,
          elements: getCourtAppointmentFields(product),
        },
        getNextInstanceSection(product),
      ],
    },
    processParticipants: {
      asAccordion: true,
      sections: [
        opposingParty(product),
        caseInsurance(product),
        debtCollectionAgencyFields(product),
        opponentLawyer(product),
        processParticipantsAuthority(product),
        prosecution(product),
        opposingLiabilityInsurance(product),
      ],
    },
    additionalProcessParticipants: {
      sections: [
        addProcessParticipant(),
        processParticipantsPeople(product),
        processParticipantsOrganizations(product),
      ],
      withoutSubmit: true,
    },
    court: {
      sections: [
        {
          elements: [
            {
              type: "CourtPicker",
              path: `${product.productClassName}.responsibleCourt`,
              isDisabled: ({ product }: { product: ContractUnion }) =>
                (product.caseCategory === "enforceClaim" && product.stage! < stagesCase.STAGE_ACTION_REQUIRED) ||
                hasActiveCourtAppointment(product.courtAppointments),
              additionalProps: {
                fullWidth: true,
                courtTypes: ["Amtsgericht", "Arbeitsgericht", "Landgericht", "Mahngericht", "Oberlandesgericht"],
              },
            },
            ...allDataPageCourtFields(product.productClassName, "responsibleCourt"),
          ],
        },
      ],
    },
  };
};
